import { useAtomValue } from "@effect/atom-react";
import * as Schema from "effect/Schema";
import { FolderTreeIcon, SearchIcon } from "lucide-react";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { isElectron } from "../env";
import { getLocalStorageItem } from "../hooks/useLocalStorage";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../keybindings";
import { cn, isMacPlatform } from "../lib/utils";
import { PRAXA_CONTROL_MATERIAL_VERSION, PRAXA_CONTROL_SHELL_VERSION } from "../praxaControlShell";
import { primaryServerKeybindingsAtom } from "../state/server";
import { useClientSettings } from "../hooks/useSettings";
import { useProjects } from "../state/entities";
import ThreadSidebar from "./Sidebar";
import ThreadSidebarV2 from "./SidebarV2";
import {
  persistProjectFilesSidebarOpen,
  ProjectFilesSidebar,
  readProjectFilesSidebarOpen,
} from "./ProjectFilesSidebar";
import { isProjectFilesSidebarLayoutAvailable } from "./projectFilesSidebarState";
import { useSidebarStageBackdropVariant } from "./SidebarStageBackdrop";
import {
  resolveInitialThreadSidebarWidth,
  resolveThreadSidebarMaximumWidth,
  THREAD_MAIN_CONTENT_MIN_WIDTH,
  THREAD_SIDEBAR_MIN_WIDTH,
  THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
} from "./threadSidebarWidth";
import {
  Sidebar,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
  useSidebarVisibility,
} from "./ui/sidebar";
import { Button } from "./ui/button";
import { CommandDialogTrigger } from "./ui/command";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

const MACOS_SIDEBAR_CONTROL_LEFT_INSET = "80px";

function readThreadSidebarWidth(viewportWidth: number): number {
  try {
    return resolveInitialThreadSidebarWidth(
      getLocalStorageItem(THREAD_SIDEBAR_WIDTH_STORAGE_KEY, Schema.Finite),
      viewportWidth,
    );
  } catch (error) {
    console.error("Could not read persisted thread sidebar width.", error);
    return resolveInitialThreadSidebarWidth(null, viewportWidth);
  }
}

function SidebarControl({
  projectFilesAvailable,
  projectFilesLayoutAvailable,
  projectFilesOpen,
  onToggleProjectFiles,
}: {
  readonly projectFilesAvailable: boolean;
  readonly projectFilesLayoutAvailable: boolean;
  readonly projectFilesOpen: boolean;
  readonly onToggleProjectFiles: () => void;
}) {
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const { toggleSidebar } = useSidebar();
  const isSidebarVisible = useSidebarVisibility();
  const stageBackdropVariant = useSidebarStageBackdropVariant();
  const sidebarShortcutLabel = shortcutLabelForCommand(keybindings, "sidebar.toggle");
  const searchShortcutLabel = shortcutLabelForCommand(keybindings, "commandPalette.toggle");

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (
        event.target instanceof HTMLElement &&
        event.target.closest("[data-keybinding-capture]")
      ) {
        return;
      }
      if (resolveShortcutCommand(event, keybindings) !== "sidebar.toggle") return;

      event.preventDefault();
      event.stopPropagation();
      toggleSidebar();
    };

    // Capture before focused editors consume commands such as Mod+B for rich-text formatting.
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [keybindings, toggleSidebar]);

  return (
    <div
      className="pointer-events-none fixed left-[var(--workspace-controls-left)] top-[var(--workspace-controls-top)] z-50 flex h-[var(--workspace-topbar-height)] items-center gap-[var(--workspace-titlebar-control-gap)]"
      data-sidebar-control=""
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarTrigger
              className={cn(
                "pointer-events-auto",
                !isElectron &&
                  isSidebarVisible &&
                  stageBackdropVariant &&
                  "[:hover,[data-pressed]]:bg-white/15 focus-visible:ring-white/90 focus-visible:ring-offset-blue-700 [&_svg]:stroke-white/90! [&_svg]:opacity-100! [&_svg]:hover:stroke-white!",
              )}
              aria-label="Toggle main sidebar"
            />
          }
        />
        <TooltipPopup side="bottom">
          {isSidebarVisible ? "Hide" : "Show"} sidebar
          {sidebarShortcutLabel ? ` (${sidebarShortcutLabel})` : ""}
        </TooltipPopup>
      </Tooltip>
      {projectFilesLayoutAvailable ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                aria-label="Toggle project files sidebar"
                aria-pressed={projectFilesOpen}
                className="pointer-events-auto size-[var(--workspace-titlebar-control-size)]! [-webkit-app-region:no-drag]"
                data-testid="project-files-sidebar-trigger"
                disabled={!projectFilesAvailable}
                onClick={onToggleProjectFiles}
                size="icon"
                variant="ghost"
              />
            }
          >
            <FolderTreeIcon aria-hidden className="size-3.5" />
          </TooltipTrigger>
          <TooltipPopup side="bottom">
            {projectFilesAvailable
              ? `${projectFilesOpen ? "Hide" : "Show"} project files`
              : "Add a project to browse files"}
          </TooltipPopup>
        </Tooltip>
      ) : null}
      <Tooltip>
        <TooltipTrigger
          render={
            <CommandDialogTrigger
              render={
                <Button
                  aria-label="Search threads and commands"
                  className="pointer-events-auto size-[var(--workspace-titlebar-control-size)]! [-webkit-app-region:no-drag]"
                  data-testid="command-palette-trigger"
                  size="icon"
                  variant="ghost"
                />
              }
            >
              <SearchIcon aria-hidden className="size-3.5" />
            </CommandDialogTrigger>
          }
        />
        <TooltipPopup side="bottom">
          Search{searchShortcutLabel ? ` (${searchShortcutLabel})` : ""}
        </TooltipPopup>
      </Tooltip>
    </div>
  );
}

export function AppSidebarLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const sidebarV2Enabled = useClientSettings((settings) => settings.sidebarV2Enabled);
  // Settings routes render the settings nav, which lives in the v1 component
  // and is identical for both sidebars — so v1 stays mounted there.
  const pathname = useLocation({ select: (location) => location.pathname });
  const isOnSettings = pathname === "/settings" || pathname.startsWith("/settings/");
  const useSidebarV2 = sidebarV2Enabled && !isOnSettings;
  const useSidebarV2Theme = useSidebarV2 || isOnSettings;
  const isMacosDesktop = isElectron && isMacPlatform(navigator.platform);
  const projects = useProjects();
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [sidebarWidth, setSidebarWidth] = useState(() => readThreadSidebarWidth(window.innerWidth));
  const [projectFilesOpen, setProjectFilesOpen] = useState(readProjectFilesSidebarOpen);
  const projectFilesAvailable = projects.length > 0;
  const projectFilesLayoutAvailable = isProjectFilesSidebarLayoutAvailable(viewportWidth);
  const showProjectFilesSidebar =
    projectFilesOpen && projectFilesAvailable && projectFilesLayoutAvailable;
  const sidebarMaximumWidth = resolveThreadSidebarMaximumWidth(viewportWidth);
  const [isWindowFullscreen, setIsWindowFullscreen] = useState(() => {
    const getWindowFullscreenState = window.desktopBridge?.getWindowFullscreenState;
    return isMacosDesktop && typeof getWindowFullscreenState === "function"
      ? getWindowFullscreenState()
      : false;
  });
  const sidebarProviderStyle = {
    "--sidebar-width": `${sidebarWidth}px`,
    "--workspace-titlebar-content-left": projectFilesLayoutAvailable
      ? "calc(var(--workspace-controls-left) + var(--workspace-titlebar-control-size) + var(--workspace-titlebar-control-gap) + var(--workspace-titlebar-control-size) + var(--workspace-titlebar-control-gap) + var(--workspace-titlebar-control-size) + var(--workspace-titlebar-control-gap))"
      : "calc(var(--workspace-controls-left) + var(--workspace-titlebar-control-size) + var(--workspace-titlebar-control-gap) + var(--workspace-titlebar-control-size) + var(--workspace-titlebar-control-gap))",
    ...(isMacosDesktop && !isWindowFullscreen
      ? { "--workspace-controls-left": MACOS_SIDEBAR_CONTROL_LEFT_INSET }
      : {}),
  } as CSSProperties;

  useEffect(() => {
    let frame = 0;
    const handleResize = () => {
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setViewportWidth(window.innerWidth);
      });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    setSidebarWidth(readThreadSidebarWidth(viewportWidth));
  }, [viewportWidth]);

  useEffect(() => {
    if (!isMacosDesktop) return;
    const bridge = window.desktopBridge;
    if (!bridge) return;
    const { getWindowFullscreenState, onWindowFullscreenStateChange } = bridge;
    if (
      typeof getWindowFullscreenState !== "function" ||
      typeof onWindowFullscreenStateChange !== "function"
    ) {
      return;
    }

    const unsubscribe = onWindowFullscreenStateChange(setIsWindowFullscreen);
    setIsWindowFullscreen(getWindowFullscreenState());
    return unsubscribe;
  }, [isMacosDesktop]);

  useEffect(() => {
    const onMenuAction = window.desktopBridge?.onMenuAction;
    if (typeof onMenuAction !== "function") {
      return;
    }

    const unsubscribe = onMenuAction((action) => {
      if (action === "open-settings") {
        const isSettingsRoute = /^\/settings(\/|$)/.test(pathname);
        if (!isSettingsRoute) {
          void navigate({ to: "/settings" });
        }
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [navigate, pathname]);

  return (
    <SidebarProvider
      className="h-dvh! min-h-0!"
      data-thread-sidebar-width={sidebarWidth}
      data-praxa-control-material={PRAXA_CONTROL_MATERIAL_VERSION}
      data-praxa-control-shell={PRAXA_CONTROL_SHELL_VERSION}
      defaultOpen
      style={sidebarProviderStyle}
    >
      <Sidebar
        side="left"
        collapsible="offcanvas"
        data-app-sidebar=""
        data-sidebar-version={useSidebarV2Theme ? "v2" : "v1"}
        className="border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
        resizable={{
          maxWidth: sidebarMaximumWidth,
          minWidth: THREAD_SIDEBAR_MIN_WIDTH,
          shouldAcceptWidth: ({ currentWidth, nextWidth, wrapper }) =>
            nextWidth <= currentWidth ||
            wrapper.clientWidth - nextWidth >= THREAD_MAIN_CONTENT_MIN_WIDTH,
          storageKey: THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
          onResize: setSidebarWidth,
        }}
      >
        {useSidebarV2 ? <ThreadSidebarV2 /> : <ThreadSidebar />}
        <SidebarRail />
      </Sidebar>
      {showProjectFilesSidebar ? (
        <ProjectFilesSidebar viewportWidth={viewportWidth} threadSidebarWidth={sidebarWidth} />
      ) : null}
      {children}
      <SidebarControl
        projectFilesAvailable={projectFilesAvailable}
        projectFilesLayoutAvailable={projectFilesLayoutAvailable}
        projectFilesOpen={showProjectFilesSidebar}
        onToggleProjectFiles={() => {
          setProjectFilesOpen((current) => {
            const next = !current;
            persistProjectFilesSidebarOpen(next);
            return next;
          });
        }}
      />
    </SidebarProvider>
  );
}
